import { StyleSheet } from 'react-native';
import type { ThemeColors } from '../../context/ThemeContext';

/** Height of the rounded field and of the round mic / send button beside it. */
const FIELD_HEIGHT = 46;
/** Inner height: the field's 1px border on each side. */
const INNER_HEIGHT = FIELD_HEIGHT - 2;
/** The input's one-line height, and the cap it grows to before scrolling
 *  (the desktop composer's `maxHeight`). */
export const MOBILE_INPUT_MIN_HEIGHT = INNER_HEIGHT;
export const MOBILE_INPUT_MAX_HEIGHT = 120;

/**
 * CORE-311 — the composer on a phone-width web viewport: attach, input and emoji
 * share one rounded field, the round mic / send button stays outside it on the
 * right. Overrides layered onto the thread's own styles, applied only on mobile
 * web — the desktop composer keeps its layout untouched.
 */
export const makeMobileComposerStyles = (tc: ThemeColors, isDark: boolean) =>
  StyleSheet.create({
    // Bottom-aligned: as the input grows to several lines, the buttons stay
    // on its last line instead of floating up to the middle.
    composer: { alignItems: 'flex-end', gap: 12, paddingHorizontal: 16 },
    field: {
      flex: 1, minHeight: FIELD_HEIGHT, borderRadius: FIELD_HEIGHT / 2,
      borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      backgroundColor: tc.surfaceWarm, paddingHorizontal: 4,
    },
    fieldRow: { flexDirection: 'row', alignItems: 'flex-end' },
    // Staged attachments inside the field, above the text line.
    stagedStrip: { paddingHorizontal: 6, paddingTop: 8, paddingBottom: 2 },
    // Inside the thumbnail's corner, dark with a white rim: readable on any
    // picture, and not clipped by the field's rounded edge.
    stagedRemove: {
      top: 4, right: 4, width: 22, height: 22, borderRadius: 11,
      backgroundColor: 'rgba(0,0,0,0.6)', borderWidth: 1.5, borderColor: '#fff',
    },
    // The field draws the surface now; the input is just the text line.
    // 44px box − 24px line = 10px top/bottom, so a single line stays centred.
    fieldInput: {
      backgroundColor: 'transparent', borderRadius: 0,
      minHeight: INNER_HEIGHT, paddingVertical: 10, paddingHorizontal: 4,
    },
    // No attach button in front (text-only chats): keep the text off the curve.
    fieldInputLeading: { paddingLeft: 14 },
    fieldBtn: { width: 40, height: INNER_HEIGHT },
    sendBtn: { width: FIELD_HEIGHT, height: FIELD_HEIGHT, borderRadius: FIELD_HEIGHT / 2 },
  });
